import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import BackgroundDecorations from '@/components/feature/BackgroundDecorations';
import ScrollReveal from '@/components/feature/ScrollReveal';
import { RailwayTiesPattern, RailwayTrackNetwork } from '@/components/feature/RailwayPatterns';
import { achievements } from '@/mocks/achievements';
import {
  TAB_KEYS,
  TAB_LABELS,
  tabSidebarMap,
  contentMap,
  type TabKey,
} from '@/mocks/achievement-detail-sections';
import DetailSidebar from './components/DetailSidebar';
import DetailContent from './components/DetailContent';

export default function AchievementDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [scrolled, setScrolled] = useState(false);
  const achievement = achievements.find((a) => a.id === id);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Parse tab from URL ──
  const rawTab = searchParams.get('tab') || 'video';
  const activeTab: TabKey = (TAB_KEYS as readonly string[]).includes(rawTab) ? (rawTab as TabKey) : 'video';

  // ── Parse section from URL ──
  const activeSectionId = searchParams.get('section') || null;

  // ── Auto-fill default section when navigating from external links ──
  useEffect(() => {
    const config = tabSidebarMap[activeTab];
    if (!activeSectionId && config.defaultSectionId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('section', config.defaultSectionId);
      setSearchParams(newParams, { replace: true });
    }
  }, [activeTab, activeSectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTab = useCallback(
    (tab: TabKey) => {
      const config = tabSidebarMap[tab];
      const newParams = new URLSearchParams();
      newParams.set('tab', tab);
      if (config.defaultSectionId) {
        newParams.set('section', config.defaultSectionId);
      }
      setSearchParams(newParams, { replace: true });
    },
    [setSearchParams],
  );

  const handleSectionSelect = useCallback(
    (sectionId: string) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('section', sectionId);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Resolve active content
  const activeContent = useMemo(() => {
    if (!activeSectionId) return null;
    return contentMap[activeSectionId] || null;
  }, [activeSectionId]);

  // Current tab config
  const currentTabConfig = tabSidebarMap[activeTab];
  const hasSidebar = currentTabConfig.sidebarItems.length > 0;

  // ── 404 State ──
  if (!achievement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center px-4">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5 text-foreground-300">
            <i className="ri-error-warning-line text-5xl" />
          </div>
          <h1 className="text-xl font-bold text-foreground-900 font-heading mb-2">成果未找到</h1>
          <p className="text-foreground-500 mb-7">
            未找到ID为 {id} 的成果，请返回列表重新选择
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </div>
            返回成果列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background-50 relative flex flex-col overflow-hidden">
      {/* ═══════ Background Decorations ═══════ */}
      <BackgroundDecorations variant="achievement" />
      <RailwayTrackNetwork />

      {/* ═══════ Fixed Header (Logo + Name only) ═══════ */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 overflow-hidden ${
          scrolled
            ? 'bg-primary-500/95 backdrop-blur-md shadow-[0_2px_16px_rgba(139,1,1,0.25)]'
            : 'bg-primary-500 shadow-[0_2px_0_rgba(255,255,255,0.2),0_4px_16px_rgba(255,255,255,0.08)]'
        }`}
      >
        {/* 高铁列车动画 */}
        <img src="/高铁(1).svg" alt="" className="train-animate absolute bottom-2 pointer-events-none h-[14px] w-auto" />

        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-white/20" />
        <div className="flex items-center h-[72px] px-6 md:px-10 max-w-[1440px] mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" className="flex items-center shrink-0">
              <div className="w-auto h-10 md:h-11 flex items-center justify-center shrink-0">
                <img
                  src="https://storage.readdy-site.link/project_files/9e4a4ca5-4366-4703-9301-bafa1f14e1f9/61b8d58b-ccfc-4ad3-8bde-a9818c85bf50_compressed_4eb3455d6c1177e0e2b6c9460a34b69a.webp"
                  alt="华东交通大学校徽"
                  className="h-full w-auto object-contain"
                />
              </div>
            </Link>
            <span className="block w-[2px] h-6 bg-white/25 shrink-0" />
            <span className="text-[17px] font-semibold text-white/90 truncate max-w-[400px] md:max-w-[629px]">
              {achievement.name}
            </span>
          </div>
        </div>
      </header>

      {/* ═══════ Tab Navigation Row ═══════ */}
      <div className="pt-[72px] bg-[#C92020]">
        <div className="max-w-[1440px] mx-auto overflow-x-auto scrollbar-hide">
          <div className="flex items-center min-w-max">
            <Link
              to="/"
              className="relative px-6 py-3 text-sm font-bold font-heading transition-all duration-200 whitespace-nowrap text-white/80 hover:text-white hover:bg-primary-700/50"
            >
              首页
            </Link>
            {TAB_KEYS.map((tab) => (
              <button
                key={tab}
                onClick={() => setTab(tab)}
                className={`relative px-6 py-3 text-sm font-bold font-heading transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  tab === activeTab
                    ? 'bg-[#D4A017] text-white'
                    : 'text-white/80 hover:text-white hover:bg-primary-700/50'
                }`}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════ Main Layout ═══════ */}
      <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar */}
          {hasSidebar && (
            <DetailSidebar
              items={currentTabConfig.sidebarItems}
              activeId={activeSectionId}
              onSelect={handleSectionSelect}
              tabLabel={TAB_LABELS[activeTab]}
            />
          )}

          {/* Right Content Area */}
          <main className="flex-1 min-w-0 overflow-y-auto px-6 md:px-10 pt-4 pb-6 md:pt-5 md:pb-8">
            {/* Breadcrumb inside content area */}
            <nav className="flex items-center gap-1.5 text-xs mb-4">
              <Link to="/" className="text-foreground-400 hover:text-primary-400 transition-colors duration-200">首页</Link>
              <span className="text-background-300">&gt;</span>
              <span className="text-foreground-400">{achievement.category}</span>
              <span className="text-background-300">&gt;</span>
              <span className="text-foreground-500 truncate max-w-[240px]">{achievement.name}</span>
              <span className="text-background-300">&gt;</span>
              <span className="text-foreground-600 font-medium">{TAB_LABELS[activeTab]}</span>
            </nav>

            <div key={activeSectionId || activeTab} className="fly-in-up">
              {activeSectionId ? (
                <DetailContent content={activeContent} />
              ) : (
                <div className="flex items-center justify-center h-full text-center text-foreground-400 relative">
                  <RailwayTiesPattern />
                  <div className="relative z-[1]">
                    <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-arrow-left-circle-line text-4xl" />
                    </div>
                    <p className="text-sm">请从左侧目录选择内容查看</p>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>

    </div>
  );
}